import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import DramaCard from '@/components/feature/DramaCard';
import { useToast } from '@/hooks/useToast';
import { dramas } from '@/mocks/dramas';
import type { Episode } from '@/types/drama';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const drama = dramas.find((d) => d.id === id);
  const { toast, showToast } = useToast();
  const [currentEp, setCurrentEp] = useState(1);

  const episodes = useMemo<Episode[]>(() => {
    if (!drama) return [];
    return Array.from({ length: drama.totalEpisodes }, (_, i) => ({
      number: i + 1,
      title: `第 ${i + 1} 集`,
      duration: `${38 + (i % 5) * 3} 分钟`,
    }));
  }, [drama]);

  const related = useMemo(() => {
    if (!drama) return [];
    return dramas
      .filter((d) => d.id !== drama.id)
      .map((d) => {
        let score = d.category === drama.category ? 1 : 0;
        score += d.tags.filter((t) => drama.tags.includes(t)).length;
        return { d, score };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
      .map((item) => item.d);
  }, [drama]);

  useEffect(() => {
    if (drama) {
      document.title = `${drama.title} - 善良的小姨子在线播放`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute('content', drama.synopsis);
    }
  }, [drama]);

  useEffect(() => {
    setCurrentEp(1);
  }, [id]);

  if (!drama) {
    return (
      <Layout>
        <div className="pt-16 md:pt-[68px] px-4 md:px-16 lg:px-24 py-24 flex flex-col items-center text-center">
          <i className="ri-file-unknow-line text-6xl text-foreground-300" />
          <h1 className="font-heading font-bold text-foreground-950 text-2xl mt-5">该剧集信息暂时不可用</h1>
          <p className="text-foreground-600 text-sm mt-3">你访问的剧集可能已下架或链接有误。</p>
          <Link
            to="/category"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-arrow-left-line" />
            返回剧集列表
          </Link>
        </div>
      </Layout>
    );
  }

  const handleShare = (type: string) => {
    const link = window.location.href;
    if (type === '微博') {
      showToast('正在打开微博分享…');
    } else if (type === '微信') {
      showToast('已复制链接，可粘贴到微信分享');
      if (navigator.clipboard) navigator.clipboard.writeText(link).catch(() => {});
    } else {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(link).then(
          () => showToast('链接已复制到剪贴板'),
          () => showToast('复制失败，请手动复制地址栏链接'),
        );
      } else {
        showToast('当前环境不支持自动复制，请手动复制');
      }
    }
  };

  return (
    <Layout>
      {toast}
      <div className="pt-16 md:pt-[68px]">
        <section className="relative w-full">
          <div className="absolute inset-0">
            <img
              src={drama.banner}
              alt={`${drama.title}背景图`}
              title={`${drama.title} 在线播放`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/85 to-background-50/40" />
          </div>
          <div className="relative z-10 w-full px-4 md:px-16 lg:px-24 pt-8 md:pt-12 pb-8 md:pb-10">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-600 mb-6">
              <Link to="/" className="hover:text-primary-700 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <Link to="/category" className="hover:text-primary-700 transition-colors">分类</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-foreground-800 line-clamp-1">{drama.title}</span>
            </nav>

            <div className="flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="w-40 sm:w-48 md:w-56 shrink-0">
                <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-100">
                  <img
                    src={drama.cover}
                    alt={`${drama.title}封面`}
                    title={`${drama.title} 剧情介绍`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  <span className="rounded-full bg-primary-100 text-primary-800 text-[11px] px-3 py-1 whitespace-nowrap">
                    {drama.category}
                  </span>
                  <span
                    className={`rounded-full text-[11px] px-3 py-1 whitespace-nowrap ${
                      drama.status === '更新中'
                        ? 'bg-accent-100 text-accent-900'
                        : 'bg-background-200 text-foreground-700'
                    }`}
                  >
                    {drama.status} · 更新至 {drama.updatedEpisodes} 集
                  </span>
                </div>
                <h1 className="font-heading font-extrabold text-foreground-950 text-2xl md:text-4xl">{drama.title}</h1>
                <p className="text-accent-700 font-medium text-sm md:text-base mt-2">{drama.subtitle}</p>
                <p className="text-foreground-700 text-sm leading-relaxed mt-4 max-w-3xl">{drama.synopsis}</p>

                <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
                  <div>
                    <dt className="text-xs text-foreground-500">类型</dt>
                    <dd className="text-sm text-foreground-900 mt-1 truncate">{drama.category}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-500">更新日期</dt>
                    <dd className="text-sm text-foreground-900 mt-1">{drama.releaseDate}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-500">总集数</dt>
                    <dd className="text-sm text-foreground-900 mt-1">{drama.totalEpisodes} 集</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-500">导演</dt>
                    <dd className="text-sm text-foreground-900 mt-1 truncate">{drama.director}</dd>
                  </div>
                </dl>
                <p className="text-xs text-foreground-500 mt-4">主演：{drama.cast.join('、')}</p>

                <div className="flex flex-wrap gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => showToast('播放功能为前端演示，真实播放接口待接入')}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-play-fill text-lg" />
                    立即播放
                  </button>
                  <div className="relative group">
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <i className="ri-share-forward-line text-lg" />
                      分享
                    </button>
                    <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 absolute left-0 top-full mt-2 z-20 w-40 rounded-lg border border-background-200/70 bg-background-50 p-2 transition-all">
                      <button type="button" onClick={() => handleShare('复制链接')} className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 whitespace-nowrap cursor-pointer">
                        <i className="ri-link" /> 复制链接
                      </button>
                      <button type="button" onClick={() => handleShare('微博')} className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 whitespace-nowrap cursor-pointer">
                        <i className="ri-weibo-fill" /> 分享到微博
                      </button>
                      <button type="button" onClick={() => handleShare('微信')} className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 whitespace-nowrap cursor-pointer">
                        <i className="ri-wechat-fill" /> 分享到微信
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-16 lg:px-24 py-6 md:py-10">
          <div className="rounded-lg overflow-hidden border border-background-200/70 bg-foreground-950">
            <div className="relative w-full aspect-video">
              <img
                src={drama.banner}
                alt={`${drama.title}播放预览`}
                className="absolute inset-0 w-full h-full object-cover object-top opacity-40"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <button
                  type="button"
                  onClick={() => showToast('播放功能为前端演示，真实播放接口待接入')}
                  className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary-500/90 text-background-50 flex items-center justify-center text-3xl md:text-4xl hover:bg-primary-500 transition-colors cursor-pointer"
                  aria-label="播放"
                >
                  <i className="ri-play-fill" />
                </button>
                <p className="mt-4 text-background-50/90 text-sm md:text-base font-medium">
                  正在播放：{drama.title} · 第 {currentEp} 集
                </p>
                <p className="mt-2 text-xs text-background-50/60">
                  <i className="ri-information-line mr-1" />
                  播放为前端演示，真实播放接口待接入
                </p>
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-background-50/20">
                <div className="h-full w-1/4 bg-accent-500" />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-16 lg:px-24 pb-4">
          <div className="flex items-center gap-2 mb-5">
            <span className="w-1 h-5 rounded-full bg-accent-500" />
            <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">剧集列表</h2>
            <span className="text-xs text-foreground-500 ml-1">共 {drama.totalEpisodes} 集</span>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-3 lg:flex-wrap lg:overflow-visible">
            {episodes.map((ep) => {
              const available = ep.number <= drama.updatedEpisodes;
              const active = ep.number === currentEp;
              return (
                <button
                  key={ep.number}
                  type="button"
                  onClick={() => {
                    setCurrentEp(ep.number);
                    showToast(`已切换到 第 ${ep.number} 集（演示）`);
                  }}
                  className={`shrink-0 w-28 rounded-md border px-3 py-3 text-left transition-colors cursor-pointer ${
                    active
                      ? 'border-primary-500 bg-primary-50'
                      : available
                        ? 'border-background-200/70 bg-background-50 hover:border-primary-300'
                        : 'border-background-200/70 bg-background-100 opacity-60'
                  }`}
                >
                  <span className={`block text-sm font-medium ${active ? 'text-primary-700' : 'text-foreground-900'}`}>
                    {ep.title}
                  </span>
                  <span className="flex items-center gap-1 text-[11px] text-foreground-500 mt-1">
                    <i className="ri-time-line" />
                    {available ? ep.duration : '未更新'}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="w-full px-4 md:px-16 lg:px-24 py-10 md:py-14">
          <div className="flex items-center gap-2 mb-5">
            <span className="w-1 h-5 rounded-full bg-secondary-500" />
            <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">剧情介绍</h2>
          </div>
          <div className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-8">
            <p className="text-foreground-700 text-sm md:text-base leading-loose">{drama.description}</p>
            <div className="flex flex-wrap gap-2 mt-6">
              {drama.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-secondary-100 text-secondary-900 text-xs px-3 py-1 whitespace-nowrap">
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-16 lg:px-24 pb-16 md:pb-20">
          <div className="flex items-center gap-2 mb-6">
            <span className="w-1 h-5 rounded-full bg-accent-500" />
            <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">相关推荐</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
            {related.map((d) => (
              <DramaCard key={d.id} drama={d} />
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}